import * as api from '../../../shared/services/apiClient';
import { AUTH_ENDPOINTS, AUTH_STORAGE_KEYS } from '../constants/auth.constants';
import { LogoutResult } from '../types/auth.types';

import { clearSession } from './session';

/**
 * Logout Logic
 * Calls backend to determine if confirmation is needed (due to active cart).
 */
export const logout = async (explicitCartId: string | null = null): Promise<LogoutResult> => {
  try {
    const storeKey = window.location.pathname.split('/')[1];
    const scopedKey = storeKey ? `${AUTH_STORAGE_KEYS.CART_ID_PREFIX}${storeKey}` : AUTH_STORAGE_KEYS.CART_ID;

    // Send cartId to backend since it's stored in browser storage, not cookies
    let cartId = (explicitCartId && explicitCartId !== 'null')
      ? explicitCartId
      : (localStorage.getItem(scopedKey) || sessionStorage.getItem(scopedKey));

    // Fallback to legacy generic 'cartId' if scoped doesn't exist
    if (!cartId) {
      cartId = localStorage.getItem(AUTH_STORAGE_KEYS.CART_ID) || sessionStorage.getItem(AUTH_STORAGE_KEYS.CART_ID);
    }

    if (!cartId) {
      console.warn('[Logout] No cartId found. Backend will skip cart confirmation.');
    }
    const response = await api.post(AUTH_ENDPOINTS.LOGOUT, { confirm: false, cartId: cartId || null });

    // Status 202 means confirmation required (active cart found)
    if (response.status === 202) {
      const result = await response.json();
      return {
        needsConfirmation: true,
        hasActiveCart: result.data?.hasActiveCart || false
      };
    }

    // Otherwise, session was cleared on the backend, so we clear locally
    clearSession();
    return { needsConfirmation: false, hasActiveCart: false };
  } catch (_error: unknown) {
    // Fail-safe: clear session locally if backend call fails
    clearSession();
    return { needsConfirmation: false, hasActiveCart: false };
  }
};

/**
 * Confirm Logout (Force clear session)
 */
export const confirmLogout = async (explicitCartId: string | null = null): Promise<boolean> => {
  try {
    const storeKey = window.location.pathname.split('/')[1];
    const scopedKey = storeKey ? `${AUTH_STORAGE_KEYS.CART_ID_PREFIX}${storeKey}` : AUTH_STORAGE_KEYS.CART_ID;

    let cartId = (explicitCartId && explicitCartId !== 'null')
      ? explicitCartId
      : (localStorage.getItem(scopedKey) || sessionStorage.getItem(scopedKey));

    if (!cartId) {
      cartId = localStorage.getItem(AUTH_STORAGE_KEYS.CART_ID) || sessionStorage.getItem(AUTH_STORAGE_KEYS.CART_ID);
    }

    await api.post(AUTH_ENDPOINTS.LOGOUT, { confirm: true, cartId: cartId || null });
  } catch {
    // Ignore error
  }
  clearSession();
  return true;
};
